import { type ReactNode } from 'react';

export interface TimelineSectionProps {
  /**
   * The label displayed vertically on the left side of the section
   */
  label: string;

  /**
   * Content to display in the section
   */
  children: ReactNode;

  /**
   * Whether this is the first section in the timeline
   * If true, the vertical line above the circle will be hidden
   * @default false
   */
  isFirst?: boolean;

  /**
   * Whether this is the last section in the timeline
   * Can be used for visual adjustments if needed
   * @default false
   */
  isLast?: boolean;

  /**
   * Additional CSS classes for the content area
   */
  contentClassName?: string;
}

/**
 * TimelineSection component for displaying a vertical timeline with a label
 *
 * Used in StepCardNode to create the "I Receive", "I Do", "I Use", "I Create" sections
 * with consistent styling and layout.
 *
 * @example
 * ```tsx
 * <TimelineSection label="I Receive" isFirst>
 *   <EventsSection {...props} />
 *   <InformationSection {...props} />
 * </TimelineSection>
 *
 * <TimelineSection label="I Do">
 *   <ProcessStepDescription {...props} />
 * </TimelineSection>
 * ```
 */
export function TimelineSection({
  label,
  children,
  isFirst = false,
  contentClassName = 'p-1'
}: TimelineSectionProps) {
  return (
    <div className="flex">
      {/* Vertical Line with Circle */}
      <div className="relative flex flex-col items-center w-6 mr-1">
        <div
          className={`w-[11px] h-[11px] rounded-full bg-white border border-line-strong absolute z-10 ${
            isFirst ? 'top-1' : '-top-1'
          }`}
        />
        <div className={`w-px h-full bg-surface-strong ${isFirst ? 'mt-1' : ''}`} />
      </div>

      {/* Vertical Label */}
      <div className="flex py-1 mr-2">
        <div
          className="bg-bg-2 border border-line-strong rounded-lg px-1 flex-1 flex items-center justify-center"
          style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
        >
          <span className="text-sm font-bold text-[#1E1E1E] leading-relaxed capitalize">
            {label}
          </span>
        </div>
      </div>

      {/* Content Area */}
      <div className={`flex-1 ${contentClassName}`}>{children}</div>
    </div>
  );
}
